import { useState, useMemo, useCallback } from 'react';
import EmployeeLink from '../components/EmployeeLink';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import {
  ChevronUp,
  ChevronDown,
  Download,
  Trash2,
  Clock,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

const PAGE_SIZE = 50;

function formatDuration(seconds) {
  if (!seconds) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function formatTime(iso, tz, timeFormat) {
  if (!iso) return '--';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: tz, hour12: timeFormat === '12h' });
}

function formatDate(iso, tz) {
  if (!iso) return '--';
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric', timeZone: tz });
}

function SortIcon({ column, sortKey, direction }) {
  if (sortKey !== column) return <ChevronUp className="w-3 h-3 text-gray-300" />;
  return direction === 'asc' ? (
    <ChevronUp className="w-3 h-3 text-blue-500" />
  ) : (
    <ChevronDown className="w-3 h-3 text-blue-500" />
  );
}

// ---- editable cell ----
function EditableCell({ value, onSave, type = 'datetime-local', tz = 'UTC', timeFormat = '12h' }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value || '');

  const handleSave = () => {
    onSave(local);
    setEditing(false);
  };

  if (!editing) {
    return (
      <span
        className="cursor-pointer hover:text-blue-600 border-b border-dashed border-transparent hover:border-blue-400 transition-colors"
        onClick={() => setEditing(true)}
      >
        {type === 'datetime-local' ? formatTime(value, tz, timeFormat) : value || '--'}
      </span>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input
        type={type}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        className="text-xs px-1.5 py-0.5 border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        autoFocus
      />
      <button onClick={handleSave} className="text-xs text-green-600 hover:text-green-700 font-medium px-1">
        Save
      </button>
      <button onClick={() => setEditing(false)} className="text-xs text-gray-400 hover:text-gray-600 px-1">
        X
      </button>
    </div>
  );
}

export default function TimeLogs() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const { timezone: tz, timeFormat } = useDisplaySettings();

  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState('startTime');
  const [sortDir, setSortDir] = useState('desc');
  const [filters, setFilters] = useState({ userId: '', projectId: '', dateFrom: '', dateTo: '', minDuration: '', maxDuration: '' });

  const { data: usersData } = useQuery({
    queryKey: ['users-list'],
    queryFn: async () => {
      const res = await api.get('/api/users');
      return res.data?.data || [];
    },
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects-list'],
    queryFn: async () => {
      const res = await api.get('/api/projects');
      return res.data?.data || [];
    },
  });

  const queryParams = useMemo(() => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(PAGE_SIZE),
      sortKey,
      sortDir,
    });
    if (filters.userId) params.set('userId', filters.userId);
    if (filters.projectId) params.set('projectId', filters.projectId);
    if (filters.dateFrom) params.set('from', filters.dateFrom);
    if (filters.dateTo) params.set('to', filters.dateTo);
    if (filters.minDuration) params.set('minDuration', filters.minDuration);
    if (filters.maxDuration) params.set('maxDuration', filters.maxDuration);
    return params.toString();
  }, [page, sortKey, sortDir, filters]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['timelogs', queryParams],
    queryFn: async () => {
      const res = await api.get(`/api/timelogs?${queryParams}`);
      return res.data;
    },
    keepPreviousData: true,
  });

  const logs = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const deleteLog = useMutation({
    mutationFn: (id) => api.delete(`/api/timelogs/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['timelogs'] }),
  });

  const updateLog = useMutation({
    mutationFn: ({ id, ...updates }) => api.patch(`/api/timelogs/${id}`, updates),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['timelogs'] }),
  });

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleExportCSV = () => {
    const csvParams = new URLSearchParams(queryParams);
    csvParams.delete('page');
    csvParams.delete('limit');
    const url = `${import.meta.env.VITE_API_URL || 'https://api.haydevs.com'}/api/timelogs/export?${csvParams.toString()}`;
    window.open(url, '_blank');
  };

  const columns = [
    { key: 'date', label: 'Date' },
    { key: 'userName', label: 'User' },
    { key: 'projectName', label: 'Project' },
    { key: 'taskTitle', label: 'Task' },
    { key: 'startTime', label: 'Start' },
    { key: 'endTime', label: 'End' },
    { key: 'duration', label: 'Duration' },
    { key: 'activityPct', label: 'Activity %' },
    ...(isAdmin ? [{ key: 'actions', label: '' }] : []),
  ];

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <p className="text-red-600 font-medium">Failed to load time logs.</p>
        <button onClick={() => refetch()} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        {isAdmin && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">User</label>
            <select
              value={filters.userId}
              onChange={(e) => handleFilterChange('userId', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Users</option>
              {(usersData || []).map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Project</label>
          <select
            value={filters.projectId}
            onChange={(e) => handleFilterChange('projectId', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Projects</option>
            {(projectsData || []).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Min Min</label>
            <input
              type="number"
              placeholder="0"
              value={filters.minDuration}
              onChange={(e) => handleFilterChange('minDuration', e.target.value)}
              className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Max Min</label>
            <input
              type="number"
              placeholder="60+"
              value={filters.maxDuration}
              onChange={(e) => handleFilterChange('maxDuration', e.target.value)}
              className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <div className="ml-auto">
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                {columns.map(({ key, label }) => (
                  <th
                    key={key}
                    onClick={() => key !== 'actions' && handleSort(key)}
                    className={`px-4 py-3 text-left text-xs font-medium text-gray-600 whitespace-nowrap ${key !== 'actions' ? 'cursor-pointer hover:text-gray-900' : ''}`}
                  >
                    {key !== 'actions' ? (
                      <div className="flex items-center gap-1">
                        {label}
                        <SortIcon column={key} sortKey={sortKey} direction={sortDir} />
                      </div>
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      {columns.map((c) => (
                        <td key={c.key} className="px-4 py-3">
                          <div className="h-3.5 bg-gray-200 rounded animate-pulse" style={{ width: `${50 + Math.random() * 40}%` }} />
                        </td>
                      ))}
                    </tr>
                  ))
                : logs.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <Clock className="w-8 h-8 text-gray-300" />
                          <p className="text-sm text-gray-400">No time logs found.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {formatDate(log.startTime, tz)}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">
                          <EmployeeLink userId={log.user?.id} name={log.user?.name || '--'} />
                        </td>
                        <td className="px-4 py-3 text-gray-600">{log.projectName || '--'}</td>
                        <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{log.taskTitle || '--'}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {isAdmin ? (
                            <EditableCell
                              value={log.startTime}
                              onSave={(v) => updateLog.mutate({ id: log.id, startTime: v })}
                              tz={tz}
                              timeFormat={timeFormat}
                            />
                          ) : (
                            formatTime(log.startTime, tz, timeFormat)
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {isAdmin ? (
                            <EditableCell
                              value={log.endTime}
                              onSave={(v) => updateLog.mutate({ id: log.id, endTime: v })}
                              tz={tz}
                              timeFormat={timeFormat}
                            />
                          ) : (
                            formatTime(log.endTime, tz, timeFormat)
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {formatDuration(log.duration)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              (log.activityPct || 0) >= 75
                                ? 'bg-green-100 text-green-700'
                                : (log.activityPct || 0) >= 40
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {Math.round(log.activityPct || 0)}%
                          </span>
                        </td>
                        {isAdmin && (
                          <td className="px-4 py-3">
                            <button
                              onClick={() => {
                                if (window.confirm('Delete this time log?')) {
                                  deleteLog.mutate(log.id);
                                }
                              }}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`w-8 h-8 rounded-md text-sm font-medium transition-colors ${
                    page === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
